import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { appSettings } from 'app/appSettings';
import { DatesWithFlorderInfoTO } from 'app/core/store/calendar/common/calendar.interface';
import { logger } from 'app/core/util/logger';
import moment from 'moment';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FlordersFilter } from '../../interfaces/florders-filter.interface';
import { OrdersTO } from '../../interfaces/order.interface';
import {
  AvailablePickingDates,
  CalculatedTruckLoad,
  DeliveryMethod, isCombinationMaterial, isPackingMaterial,
  isPalletMaterial,
  OrderDetail,
  OrderDetailTO, PalletMaterial, RECURRENCE_PATTERN_MONTHLY, RECURRENCE_PATTERN_NR_OF_DAY, RECURRENCE_PATTERN_WEEKLY, RECURRENCE_PATTERN_WHICH_DAY, RelocationDetail,
} from '../../store/edit-florder-detail/florder-detail.interface';
import { SimplifiedEmailActor } from '../../store/email-actors/email-actors.interface';
import { RecurrenceDates } from '../../store/order-detail/order-detail.interface';
import { Customer } from '../../store/session/user-context/user-context.interface';
import { util } from '../../util/util';
import { default as cachedOrder } from './responses/getOrder';
import { default as cachedOrders } from './responses/getOrders';
import { RestService } from './rest.service';

@Injectable({ providedIn: 'root' })
export class OrderRestService {
  constructor(protected restService: RestService) { }

  getOrders(filter: FlordersFilter, customers: Customer[] = null): Observable<OrdersTO> {
    if (appSettings.USE_MOCK_REST_DATA) {
      logger.log('MOCK REST getOrders');
      return of(cachedOrders as OrdersTO);
    } else {
      if (filter != null) {
        const params = this.getQueryParams({}, filter, customers, false);
        return this.restService.post<OrdersTO>(`/Order/query`, params);
      }
      return this.restService.post<OrdersTO>('/Order/query', {});
    }
  }

  getOrdersForCalendar(filter: FlordersFilter, customers: Customer[] = null): Observable<DatesWithFlorderInfoTO> {
    const params = this.getQueryParams({}, filter, customers, true);
    return this.restService.post<DatesWithFlorderInfoTO>(`/Order/listOrderNumbers`, params);
  }

  getOrdersAsDocument(filter: FlordersFilter, totalItems: number, exportType: string, customers: Customer[] = []): Observable<object> {
    if (filter != null) {
      const params = this.getQueryParams(
        { ExportType: exportType },
        {
          ...filter,
          pageNr: 1,
          pageSize: totalItems,
        },
        customers,
        false
      );
      return this.restService.getBlobData(`/Order/listOrdersAsDocument`, params);
    }
    return this.restService.getBlobData(`/Order/listOrdersAsDocument`, { ExportType: exportType });
  }

  getOrder(etmOrderNr: string, salesOrderNumber: string, customerIds: string[] = []): Observable<OrderDetailTO> {
    if (appSettings.USE_MOCK_REST_DATA) {
      logger.log('MOCK REST getOrder');
      return of(cachedOrder as OrderDetailTO);
    } else {
      let url = `/Order/get?EtmOrderNumber=${etmOrderNr}`;
      if (salesOrderNumber) {
        url += `&SalesOrderNumber=${salesOrderNumber}`;
      }

      if (customerIds && customerIds.length) {
        url += '&' + util.serializeObjectToParams({ customerId: customerIds });
      }
      return this.restService.get<OrderDetailTO>(url);
    }
  }

  getDeliveryDates(deliveryMethod: DeliveryMethod, startDate?: string): Observable<AvailablePickingDates> {
    const time = moment().format('HH:mm:ss');
    const url = `/Order/getDeliveryDates?OrderType=${deliveryMethod.type}&FromId=${deliveryMethod.from.id}&ToId=${deliveryMethod.to.id}&Transport=${deliveryMethod.shippingCondition && deliveryMethod.shippingCondition.id
      }&NumberOfMonths=6&time=${time}`;
    return this.restService.get<AvailablePickingDates>(url);
  }

  createOrder(customerId: string, florderDetail: OrderDetail, emailAddresses: SimplifiedEmailActor[]): Observable<OrderDetail> {
    const postData = this.createPostData(florderDetail, emailAddresses);
    return this.restService.post<OrderDetail>('/Order/create', postData, customerId);
  }

  /**
   * Calculates the load of the truck
   * @param {OrderDetail} florderDetail
   * @returns {Observable<OrderDetail>}
   */
  checkPalletFloorQuantitySuccessDummy(florderDetail: OrderDetail): Observable<CalculatedTruckLoad> {
    // Don't do rest call in case of only packings.  Will always be good & quantity == 0
    return of({
      orderLineInfo: {
        fullTruck: false,
        quantity: florderDetail.materials
          .filter(m => isPalletMaterial(m))
          .map(m => m as PalletMaterial)
          .reduce((res, m) => res + util.toNumber(m.numberOfPallets), 0) + '',
        maxAmountOfPalletExchangeAvailable: 0,
        orderLines: [],
        loadedEpp: null,
        maxEpp: null,
      },
      message: {
        description: null,
        type: null,
      },
    });
  }

  /**
   * Calculates the load of the truck
   * @param {OrderDetail} florderDetail
   * @param type order or relocation
   * @returns {Observable<OrderDetail>}
   */
  checkPalletFloorQuantity(florderDetail: OrderDetail, type: String = 'Order'): Observable<CalculatedTruckLoad> {
    if (florderDetail.materials.find(m => m.type === 'combination') == null) {
      // Don't do rest call in case of only packings.  Will always be good & quantity == 0
      return this.checkPalletFloorQuantitySuccessDummy(florderDetail);
    }
    let postData: any = this.createPostData(florderDetail);
    postData = util.removeNullOrUndefined(postData);
    postData.requestedUnloadingDate = util.formatDate(new Date());
    return this.restService.post<any>('/' + type + '/checkPalletFloorQuantity', postData).pipe(
      catchError(error => {
        return throwError(() => ({
          status: error.status,
          result: error,
        }));
      })
    );
  }

  getRecurrenceDates(orderDetail: OrderDetail): Observable<RecurrenceDates> {
    const recurrence = orderDetail.planning.recurrence;
    const startDate = util
      .parseStringToMoment(orderDetail.planning.loadingDate as string)
      .subtract(1, 'day')
      .format(appSettings.DATE_FORMAT_MOMENT_REST);
    let postData = {
      ...this.createPostData(orderDetail),
      fullTruck: orderDetail.fullTruck,
      recurrence: {
        pattern: (recurrence.pattern === RECURRENCE_PATTERN_WHICH_DAY || recurrence.pattern === RECURRENCE_PATTERN_NR_OF_DAY)
          ? RECURRENCE_PATTERN_MONTHLY : RECURRENCE_PATTERN_WEEKLY,
        startDate,
        endDate: recurrence.endDate,
        weekly:
          (recurrence.pattern === RECURRENCE_PATTERN_WEEKLY && {
            monday: recurrence.weekly.monday,
            tuesday: recurrence.weekly.tuesday,
            wednesday: recurrence.weekly.wednesday,
            thursday: recurrence.weekly.thursday,
            friday: recurrence.weekly.friday,
            saturday: recurrence.weekly.saturday,
            sunday: recurrence.weekly.sunday,
          }) ||
          null,
        weeklyNumbers: {
          mondayRecurrenceNumber: recurrence.weekly.mondayRecurrence,
          tuesdayRecurrenceNumber: recurrence.weekly.tuesdayRecurrence,
          wednesdayRecurrenceNumber: recurrence.weekly.wednesdayRecurrence,
          thursdayRecurrenceNumber: recurrence.weekly.thursdayRecurrence,
          fridayRecurrenceNumber: recurrence.weekly.fridayRecurrence,
          saturdayRecurrenceNumber: recurrence.weekly.saturdayRecurrence,
          sundayRecurrenceNumber: recurrence.weekly.sundayRecurrence,
        },
        monthly:
          ((recurrence.pattern === RECURRENCE_PATTERN_WHICH_DAY || recurrence.pattern === RECURRENCE_PATTERN_NR_OF_DAY) && {
            monthlyRecurrenceType: recurrence.pattern,
            day: recurrence.monthly.day,
            whichDay: recurrence.monthly.which,
            weekday: recurrence.monthly.weekday,
            monthlyPeriodicity: (recurrence.pattern === RECURRENCE_PATTERN_NR_OF_DAY && recurrence.monthly.monthPeriodicy1) || recurrence.monthly.monthPeriodicy2,
          }) ||
          null,
      },
    };
    postData = util.removeNullOrUndefined(postData);
    return this.restService.post<any>('/Order/getRecurrentOrderDates', postData).pipe(
      catchError((result: HttpErrorResponse) => {
        return throwError(() => result);
      })
    );
  }

  acceptDeviation(ccrNumber: string): Observable<any> {
    return this.restService.post<any>('/Ccr/validate', {
      ccrNumber,
      approved: true,
    });
  }

  rejectDeviation(ccrNumber: string): Observable<any> {
    return this.restService.post<any>('/Ccr/validate', {
      ccrNumber,
      approved: false,
    });
  }

  getLatestOrders(selectedCustomerId: string): Observable<OrdersTO> {
    return this.restService.get<OrdersTO>(`/Order/Latest?CustomerId=${selectedCustomerId}`);
  }

  getOrderTypes(selectedCustomerId: string): Observable<any> {
    return this.restService.get<any>(`/Customer/getOrderTypes?date=${util.formatDate(new Date())}&CustomerId=${selectedCustomerId}`);
  }

  protected getQueryParams(params: any, filter: FlordersFilter, customers: Customer[] = null, transportAsArray: boolean): object {
    let customerIdsForFilter = null;
    if (filter.customers) {
      if (filter.customers.length === 0) {
        customerIdsForFilter = (customers || []).map(c => c.id);
      } else {
        customerIdsForFilter = filter.customers;
      }
    }

    let transport = null;
    if (Array.isArray(filter.shippingCondition) && filter.shippingCondition.length === 1) {
      transport = filter.shippingCondition[0];
    }

    if (typeof filter.shippingCondition === 'string') {
      transport = (filter.shippingCondition as string).trim();
    }

    transport = (transport !== 'all' && transport) || null;
    if (transport && transportAsArray) {
      transport = [transport];
    }

    const newParamsObject = {
      ...params,
      OrderType: filter.orderTypes,
      Transport: transport,
      Transporter: filter.transporter,
      OrderDateFrom: filter.florderDateFrom,
      OrderDateTo: filter.florderDateTo,
      UnloadingDateFrom: filter.unloadingDateFrom,
      UnloadingDateTo: filter.unloadingDateTo,
      PalletId: filter.palletIds,
      PackingId: filter.packingIds,
      Depot: filter.depots,
      From: filter.froms,
      To: filter.tos,
      ShipTo: filter.shipTos,
      CustomerRefNumber: filter.customerReferenceNumber || null,
      SenderRefNumber: filter.senderReferenceNumber || null,
      SalesOrderNumber: filter.salesOrderNumber || null,
      EtmOrderNumber: filter.etmDocumentNumber || null,
      Ccr: (filter.ccrYes && filter.ccrNo) || (!filter.ccrYes && !filter.ccrNo) ? null : filter.ccrYes,
      SealNumber: filter.sealNumber,
      PageNumber: filter.pageNr,
      PageSize: filter.pageSize,
      SortingColumn: filter.sortField,
      SortingType: filter.sortAscending ? 'asc' : 'desc',
      OrderStatus: filter.orderStatuses,
      CustomerId: customerIdsForFilter,
      DefinitiveDateFrom: filter.florderDefinitiveDateFrom,
      DefinitiveDateTo: filter.florderDefinitiveDateTo,
    };
    return newParamsObject;
  }

  protected createPostData(orderDetail: OrderDetail | RelocationDetail, emailAddresses?: SimplifiedEmailActor[]): any {
    const postData = {
      orderType: orderDetail.deliveryMethod.type,
      soldTo: (orderDetail.deliveryMethod.soldTo && orderDetail.deliveryMethod.soldTo.id) || undefined,
      fromId: orderDetail.deliveryMethod.from.id,
      toId: orderDetail.deliveryMethod.to.id,
      requestedUnloadingDates: orderDetail.planning.loadingDate,
      transport: (orderDetail.deliveryMethod.shippingCondition && orderDetail.deliveryMethod.shippingCondition.id) || undefined,
      globalType: '',
      transporter: orderDetail.deliveryMethod.shippingCondition && orderDetail.deliveryMethod.shippingCondition.id !== 'Z1' ? orderDetail.deliveryMethod.transporter : undefined,
      licensePlate: orderDetail.deliveryMethod.shippingCondition && orderDetail.deliveryMethod.shippingCondition.id !== 'Z1' ? orderDetail.deliveryMethod.licensePlate : undefined,
      incoterm: (orderDetail.deliveryMethod.incoterm && orderDetail.deliveryMethod.incoterm.id) || undefined,
      customerRefNumber: orderDetail.deliveryMethod.customerReferenceNumber,
      senderRefNumber: orderDetail.deliveryMethod.senderReferenceNumber,
      exchangePallets: orderDetail.planning.exchangePallets || false,
      numberExchangePallets: orderDetail.planning.exchangePallets ? orderDetail.planning.numberOfExchangePallets || 0 : 0,
      emailAddresses: emailAddresses || [],
      lineItem: orderDetail.materials.map(m => {
        return {
          lineType: m.type,
          palletId: m.type !== 'packing' ? util.cleanValue(m.palletId) : undefined,
          packingId: (isPackingMaterial(m) || isCombinationMaterial(m)) && util.cleanValue(m.packingId) || undefined,
          packingStatus: util.cleanValue(m.packingStatus),
          logisticsVarietyPacking: util.cleanValue(m.logisticsVarietyPacking),
          packingsPerPallet: isCombinationMaterial(m) ? util.cleanValue(m.packingsPerPallet) : undefined,
          numberOfPallets: !isPackingMaterial(m) ? util.cleanValue(m.numberOfPallets) : undefined,
          packingQuantity: !isPalletMaterial(m) ? util.cleanValue(m.packingQuantity) || util.toNumber(m['packingsPerPallet']) * util.toNumber(m['numberOfPallets']) : undefined,
        };
      }),
    };
    return postData;
  }
}
